// src/services/userService.js

import {
  doc,
  updateDoc,
  collection,
  getDocs,
  query,
  where,
  getDoc,
  addDoc,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';

import { db } from '@/firebase/firebase';

// ✅ Get user profile from Firestore
export async function getUserProfile(uid) {
  const userRef = doc(db, 'users', uid);
  const snap = await getDoc(userRef);
  return snap.exists() ? snap.data() : null;
}

// ✅ Update user fields in Firestore
export async function updateUserProfile(uid, updates) {
  console.log('🔄 updateUserProfile:', uid, updates);
  const userRef = doc(db, 'users', uid);
  await updateDoc(userRef, updates);
  console.log('✅ User profile updated');
}

// ✅ Save FCM token to Firestore user document
export async function saveUserFCMToken(userId, token) {
  if (!userId || !token) return;
  try {
    await setDoc(doc(db, 'users', userId), { fcmToken: token }, { merge: true });
    console.log('✅ FCM token saved to user profile');
  } catch (err) {
    console.error('❌ Failed to save FCM token:', err);
  }
}

// ✅ Log verification approval to analytics
export async function logVerificationEvent({ userId, teamId, role }) {
  console.log('📝 Logging verification event:', { userId, teamId, role });
  await addDoc(collection(db, 'verificationLogs'), {
    userId,
    teamId,
    role,
    timestamp: serverTimestamp(),
  });
  console.log('✅ Verification event logged');
}

// ✅ Admin-only action to approve or decline a coach
// When approved, also mark onboarding done so route guards stop redirecting.
export async function verifyUser(uid, status = 'verified') {
  const updates = {
    verified: status === 'verified',
    verificationStatus: status,
    verifiedAt: status === 'verified' ? serverTimestamp() : null,
    declinedAt: status === 'declined' ? serverTimestamp() : null,
  };

  // 👇 key part: unblock restricted pages for verified coaches
  if (status === 'verified') {
    updates.onboardingStatus = 'completed';
    updates.onboardingCompletedAt = serverTimestamp();
    updates.verificationDeclineReason = null; // clear any prior decline reason
  }

  try {
    console.log('🔐 Verifying user:', uid, status);
    await updateUserProfile(uid, updates);

    if (status === 'verified') {
      const userProfile = await getUserProfile(uid);
      console.log('📄 Retrieved user profile:', userProfile);

      if (userProfile) {
        await logVerificationEvent({
          userId: uid,
          teamId: userProfile.teamId || null,
          role: userProfile.role || 'coach',
        });
      }
    }

    console.log(`✅ User ${uid} marked as ${status}`);
  } catch (error) {
    console.error(`❌ Failed to update verification for user ${uid}:`, error);
    throw error;
  }
}

// ✅ Allow a user to join a team with a code (used in JoinTeam.jsx)
export async function joinTeamByCode(uid, code) {
  const inviteQuery = query(
    collection(db, 'teamInvites'),
    where('code', '==', code),
    where('status', '==', 'pending')
  );

  const snapshot = await getDocs(inviteQuery);
  if (snapshot.empty) throw new Error('Invalid invite code.');

  const invite = snapshot.docs[0].data();

  const userRef = doc(db, 'users', uid);
  await updateDoc(userRef, {
    teamId: invite.teamId,
    role: invite.role || 'assistant_coach',
  });

  console.log(`✅ ${uid} joined team ${invite.teamId}`);
}
